import { createTheme, ThemeOptions, Shadows } from '@mui/material/styles';
import { PartnerBrandingConfig } from '@/schemas/clientInitSchema';
import type {} from '@mui/x-date-pickers/themeAugmentation';

// Useful page for seeing the available options: https://mui.com/material-ui/customization/default-theme/

type MuiComponent = ThemeOptions['components'];
type MuiTypography = ThemeOptions['typography'];

export function generateTheme(partnerBrandConfig?: PartnerBrandingConfig) {
  const neutralHue = 240;
  const colors = {
    primary: 'hsl(17, 100%, 54%)',
    success: 'hsl(174, 63%, 40%)',
    error: 'hsl(0, 100%, 46%)',
    // Neutral color scale
    white: 'hsl(0, 0%, 100%)',
    neutral_50: `hsl(${neutralHue}, 8%, 95%)`,
    neutral_100: `hsl(${neutralHue}, 8%, 90%)`,
    neutral_200: `hsl(${neutralHue}, 8%, 80%)`,
    neutral_300: `hsl(${neutralHue}, 8%, 70%)`,
    neutral_400: `hsl(${neutralHue}, 8%, 60%)`,
    neutral_500: `hsl(${neutralHue}, 8%, 50%)`,
    neutral_600: `hsl(${neutralHue}, 8%, 40%)`,
    neutral_700: `hsl(${neutralHue}, 8%, 30%)`,
    neutral_800: `hsl(${neutralHue}, 8%, 20%)`,
    neutral_900: `hsl(${neutralHue}, 8%, 10%)`,
    neutral_950: `hsl(${neutralHue}, 8%, 5%)`,
    black: 'hsl(0, 0%, 0%)',
  };

  const colorSchemes = {
    light: {
      palette: {
        tonalOffset: {
          light: 0.1,
          dark: 0.08,
        },
        /** Linter thinks we're doing this because it might be undefined. In
         * reality we're doing it because it might be undefined *and* an empty
         * string here makes MUI big mad. */
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        primary: {
          main: partnerBrandConfig?.primary_color || colors.primary,
        },
        secondary: {
          main: partnerBrandConfig?.secondary_color || colors.primary,
        },
        tertiary: {
          main: partnerBrandConfig?.tertiary_color || colors.primary,
        },
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
        success: {
          main: colors.success,
        },
        error: {
          main: colors.error,
        },
        background: {
          default: colors.white, // This is the default background color for the entire app.
          paper: colors.white, // This is the background color for what MUI calls "paper" elements, like cards.
          backdrop: 'hsl(0, 0%, 0%, 0.5)', // This is the background color for things like modal backdrops.
          main: colors.white,
          secondary: colors.neutral_50,
          inverse: colors.black,
        },
        border: {
          main: colors.neutral_200,
          secondary: colors.neutral_100,
        },
        text: {
          primary: colors.neutral_900,
          secondary: colors.neutral_500,
        },
      },
    },
    dark: {
      palette: {
        tonalOffset: {
          light: 0.1,
          dark: 0.08,
        },
        primary: {
          main: partnerBrandConfig?.primary_color,
        },
        secondary: {
          main: partnerBrandConfig?.secondary_color,
        },
        tertiary: {
          main: partnerBrandConfig?.tertiary_color,
        },
        success: {
          main: colors.success,
        },
        error: {
          main: colors.error,
        },
        background: {
          default: colors.neutral_950,
          paper: colors.neutral_900,
          backdrop: 'hsl(0, 0%, 0%, 0.7)',
          main: colors.neutral_950,
          secondary: colors.neutral_900,
          inverse: colors.white,
        },
        border: {
          main: colors.neutral_700,
          secondary: colors.neutral_800,
        },
        text: {
          primary: colors.white,
          secondary: colors.neutral_200,
        },
      },
    },
  };

  const typography: MuiTypography = {
    fontFamily: ['Root Sans', 'sans-serif'].join(','),
    h1: {
      fontSize: '2.25rem', // 36px
      fontWeight: 500,
      letterSpacing: '-0.015em',
    },
    h2: {
      fontSize: '1.75rem', // 28px
      fontWeight: 500,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontSize: '1.375rem', // 22px
      fontWeight: 500,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      letterSpacing: '-0.015em',
      lineHeight: '1.5',
    },
    body1: {
      fontSize: '1.125rem', // 18px
    },
    body2: {
      fontSize: '0.875rem', // 14px
      lineHeight: '1.3',
    },
    overline: {
      fontSize: '0.75rem', // 12px
      lineHeight: '1',
      textTransform: 'none',
    },
    // Keeping the variety of typographic variants to a minimum helps us to maintain a cleaner, more consistent looking interface.
    // We currently have no need for these:
    h5: undefined,
    h6: undefined,
    subtitle1: undefined,
    subtitle2: undefined,
  };

  const shape = {
    borderRadius: 6,
  };

  const rootShadows = {
    none: 'none',
    small: `
      0px 5px 5px -3px hsl(${neutralHue} 8% 50% / 0.5),
      0px 2px 3px -1px hsl(${neutralHue} 8% 50% / 0.2)
    `,
    medium: `
      0px 6px 10px -3px hsl(${neutralHue} 8% 50% / 0.3),
      0px 2px 3px -2px hsl(${neutralHue} 8% 50% / 0.2)
    `,
    large: `
      0px 10px 15px -3px hsl(${neutralHue} 8% 50% / 0.15),
      0px 4px 6px -4px hsl(${neutralHue} 8% 50% / 0.2)
    `,
  } as const;

  // Custom shadow definitions for MUI's 24 elevation levels
  const shadows: Shadows = [
    rootShadows.none, // elevation 0
    rootShadows.small, // elevation 1
    rootShadows.small, // elevation 2
    rootShadows.small, // elevation 3
    rootShadows.small, // elevation 4
    rootShadows.small, // elevation 5
    rootShadows.small, // elevation 6
    rootShadows.small, // elevation 7
    rootShadows.small, // elevation 8
    rootShadows.medium, // elevation 9
    rootShadows.medium, // elevation 10
    rootShadows.medium, // elevation 11
    rootShadows.medium, // elevation 12
    rootShadows.medium, // elevation 13
    rootShadows.medium, // elevation 14
    rootShadows.medium, // elevation 15
    rootShadows.medium, // elevation 16
    rootShadows.large, // elevation 17
    rootShadows.large, // elevation 18
    rootShadows.large, // elevation 19
    rootShadows.large, // elevation 20
    rootShadows.large, // elevation 21
    rootShadows.large, // elevation 22
    rootShadows.large, // elevation 23
    rootShadows.large, // elevation 24
  ];

  const buttonCustomizations: MuiComponent = {
    MuiButton: {
      styleOverrides: {
        sizeLarge: {
          height: '58px',
        },
        sizeMedium: {
          height: '42px',
        },
        sizeSmall: {
          height: '38px',
        },
        root: {
          fontSize: '18px',
          textTransform: 'none',
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                borderWidth: '2px',
              },
            },
          ],
        },
      },
    },
  };

  const stackCustomizations: MuiComponent = {
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
  };

  const cardCustomizations: MuiComponent = {
    MuiCard: {
      defaultProps: {
        elevation: 20, // Overrides the default Card elevation from 1 to 20
      },
    },
  };

  const inputCustomizations: MuiComponent = {
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiPickersOutlinedInput-root.Mui-focused': {
            '& fieldset.MuiPickersOutlinedInput-notchedOutline': {
              borderColor: 'var(--mui-palette-tertiary-main)',
            },
          },
          '& .MuiFormLabel-root.Mui-focused': {
            color: 'var(--mui-palette-tertiary-main)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: 'var(--mui-palette-tertiary-main)',
            },
          },
          '& .MuiFormLabel-root.Mui-focused': {
            color: 'var(--mui-palette-tertiary-main)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', // Sets helper text size to 14px
        },
      },
    },
  };

  const selectMenu = {
    border: '1px solid var(--mui-palette-border-secondary)',
    boxShadow: 'var(--shadow-large)',
  };

  const selectMenuList = {
    paddingTop: 0,
    paddingBottom: 0,
  };

  const selectMenuListItem = {
    fontSize: '16px',
    paddingBlock: '0.75rem',
    borderTop: '1px solid var(--mui-palette-border-secondary)',
    '&:first-of-type': {
      borderTop: 'none',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent !important',
      fontWeight: '600',
      '&:hover': {
        backgroundColor: 'var(--mui-palette-action-hover) !important',
      },
    },
  };

  const menuCustomizations: MuiComponent = {
    MuiAutocomplete: {
      styleOverrides: {
        paper: selectMenu,
        listbox: selectMenuList,
        option: selectMenuListItem,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: selectMenu,
        list: selectMenuList,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: selectMenuListItem,
      },
    },
  };

  const baseCustomizations: MuiComponent = {
    MuiCssBaseline: {
      styleOverrides: `
      :root {
        --app-header-height: 60px;
        --scene-max-width: 520px;
        --ease-out-quart: cubic-bezier(0.165, 0.85, 0.45, 1);

        --shadow-none: ${rootShadows.none};
        --shadow-small: ${rootShadows.small};
        --shadow-medium: ${rootShadows.medium};
        --shadow-large: ${rootShadows.large};

        --border-radius-small: ${shape.borderRadius}px;
        --border-radius-medium: 12px;
        --border-radius-large: 16px;
      }
    `,
    },
  };

  const themeConfig = {
    cssVariables: true,
    colorSchemes: { light: colorSchemes.light }, // This allows us to force the light theme for now.
    // colorSchemes,
    typography,
    shape,
    spacing: 6,
    shadows,
    components: {
      ...baseCustomizations,
      ...buttonCustomizations,
      ...inputCustomizations,
      ...menuCustomizations,
      ...stackCustomizations,
      ...cardCustomizations,
    },
  };

  return createTheme(themeConfig);
}

export const baseTheme = generateTheme();
